import { useTranslation } from "react-i18next";
import {
  IconFileText,
  IconFileTypePdf,
  IconLoader2,
  IconX,
} from "@tabler/icons-react";
import { iosGenerateHaptic } from "../../../tools/ios";
import { HapticEffect } from "../../../interfaces";
import type { ChatAttachments as ChatAttachmentsState } from "../../../domains/gpt/hooks/useChatAttachments";

// What is attached, shown as chips above the composer and in the transcript.
// Purely presentational — `useChatAttachments` owns the list, the upload and the
// errors, and ChatInput owns the paperclip that feeds it.

// PDFs go through OCR; everything else is read as text. Announced up-front so
// the file picker does not offer what the server will refuse (it re-checks the
// bytes regardless — an extension is not a fact).
export const ACCEPTED_FILE_TYPES = [
  "application/pdf",
  "application/json",
  "application/xml",
  "text/*",
  ".rdf",
  ".bib",
  ".ris",
  ".csv",
  ".json",
  ".xml",
  ".md",
  ".txt",
].join(",");

/** The attached-files row. Renders nothing when there is nothing attached. */
export function ChatAttachmentChips({
  attachments,
  canRemove,
}: {
  attachments: ChatAttachmentsState;
  // False while a turn is streaming on the landing page: the files are shown (the
  // turn is grounding on them) but not editable.
  canRemove: boolean;
}): React.ReactElement | null {
  const { t } = useTranslation();
  if (attachments.files.length === 0) return null;

  return (
    <div className="mb-2 flex flex-wrap gap-2">
      {attachments.files.map((file) => (
        <div
          key={
            file.status === "attached"
              ? file.documentId
              : `uploading-${String(file.pickId)}`
          }
          className="flex max-w-full items-center gap-1.5 rounded-full bg-white px-3 py-1.5 shadow-[0px_2px_8px_rgba(0,0,0,0.08)]"
        >
          {file.status === "uploading" ? (
            <IconLoader2 className="size-4 shrink-0 animate-spin text-ds-mint-900" />
          ) : file.kind === "paged" ? (
            <IconFileTypePdf className="size-4 shrink-0 text-ds-mint-800" />
          ) : (
            <IconFileText className="size-4 shrink-0 text-ds-mint-800" />
          )}
          <span className="truncate font-dm-sans text-xs text-ds-mint-900">
            {file.filename}
          </span>
          {file.status === "attached" && canRemove && (
            <button
              type="button"
              aria-label={t("Remove {{file}}", { file: file.filename })}
              className="flex shrink-0 cursor-pointer items-center border-none bg-transparent p-0 text-[#7a6d65]"
              onClick={() => {
                iosGenerateHaptic(HapticEffect.SELECTION);
                attachments.onRemove(file.documentId);
              }}
            >
              <IconX className="size-4" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * The conversation's attached files, shown IN the transcript above the first turn
 * — so a file reads as part of the conversation rather than as composer chrome,
 * and is still there when the conversation is reopened (the list is the server's,
 * not session state). Tapping one opens its preview.
 *
 * Renders nothing when the conversation has no attachments, and deliberately
 * shows only SETTLED files: an upload in flight belongs to the composer, where the
 * spinner and the cancel affordance are.
 */
export function ChatAttachmentList({
  attachments,
}: {
  attachments: ChatAttachmentsState;
}): React.ReactElement | null {
  const { t } = useTranslation();
  const attached = attachments.files.flatMap((file) =>
    file.status === "attached" ? [file] : []
  );
  if (attached.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      <span className="font-dm-sans text-xs font-semibold text-[#7a6d65]">
        {t("Attached files")}
      </span>
      <div className="flex flex-wrap gap-2">
        {attached.map((file) => {
          const label = (
            <>
              {file.kind === "paged" ? (
                <IconFileTypePdf className="size-4 shrink-0 text-ds-mint-800" />
              ) : (
                <IconFileText className="size-4 shrink-0 text-ds-mint-800" />
              )}
              <span className="truncate font-dm-sans text-xs text-ds-mint-900">
                {file.filename}
              </span>
            </>
          );
          // Always tappable: a text file previews as its (server-capped) head and a
          // PDF opens in the viewer, so there is no size at which a tap leads to
          // an apology.
          return (
            <button
              key={file.documentId}
              type="button"
              className="flex max-w-full cursor-pointer items-center gap-1.5 rounded-full border-none bg-white px-3 py-1.5 shadow-[0px_2px_8px_rgba(0,0,0,0.08)]"
              onClick={() => {
                iosGenerateHaptic(HapticEffect.SELECTION);
                attachments.onPreview(file.documentId);
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
